import { SnackbarProvider } from "notistack";
import { styled } from "@mui/material/styles";
import { forwardRef } from "react";

// Custom glassmorphism snackbar component

// Custom glassmorphism snackbar component
const GlassmorphismSnackbar = styled("div")(({ theme, variant }) => ({
  borderRadius: "9px",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(20px)",
  border: "1px solid rgba(40, 40, 40, 0.4)",
  boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.8),
      0 4px 16px rgba(0, 0, 0, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.45)
    `,
  padding: "12px 12px",
  minWidth: "300px",
  maxWidth: "500px",
  position: "relative",
  overflow: "hidden",

  // Variant-specific glassmorphism backgrounds - more saturated for dark background
  ...(variant === "success" && {
    background:
      "linear-gradient(135deg, rgba(46, 204, 113, 0.65) 0%, rgba(39, 174, 96, 0.45) 100%)",
    borderColor: "rgba(46, 204, 113, 0.6)",
  }),
  ...(variant === "error" && {
    background:
      "linear-gradient(135deg, rgba(231, 76, 60, 0.65) 0%, rgba(192, 57, 43, 0.45) 100%)",
    borderColor: "rgba(231, 76, 60, 0.6)",
  }),
  ...(variant === "warning" && {
    background:
      "linear-gradient(135deg, rgba(241, 196, 15, 0.65) 0%, rgba(230, 126, 34, 0.45) 100%)",
    borderColor: "rgba(241, 196, 15, 0.6)",
  }),
  ...(variant === "info" && {
    background:
      "linear-gradient(135deg, rgba(52, 152, 219, 0.65) 0%, rgba(41, 128, 185, 0.45) 100%)",
    borderColor: "rgba(52, 152, 219, 0.6)",
  }),
  ...(variant === "default" && {
    background:
      "linear-gradient(135deg, rgba(149, 165, 166, 0.25) 0%, rgba(127, 140, 141, 0.1) 100%)",
    borderColor: "rgba(149, 165, 166, 0.4)",
  }),

  // Subtle shimmer effect - darker for black background
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent)",
    animation: "shimmer 3s infinite",
    pointerEvents: "none",
  },

  "@keyframes shimmer": {
    "0%": { left: "-100%" },
    "100%": { left: "100%" },
  },

  // Message text styling - brighter for dark background
  "& .SnackbarContent-message": {
    color: "rgba(255, 255, 255, 0.95)",
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: 1.4,
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.5)",
  },

  // Action buttons styling - optimized for dark background
  "& .SnackbarContent-action": {
    "& button": {
      color: "rgba(255, 255, 255, 0.8)",
      borderRadius: "8px",
      padding: "4px 8px",
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        transform: "scale(1.05)",
      },
    },
  },

  // Hover effects - enhanced for dark background
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `
        0 12px 40px rgba(0, 0, 0, 0.6),
        0 6px 20px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.1)
      `,
    borderColor: "rgba(255, 255, 255, 0.2)",
  },

  // Entrance animation
  animation: "slideInGlass 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",

  "@keyframes slideInGlass": {
    "0%": {
      opacity: 0,
      transform: "translateX(-100%) scale(0.8)",
      filter: "blur(10px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0) scale(1)",
      filter: "blur(0px)",
    },
  },

  // Responsive design
  [theme.breakpoints.down("sm")]: {
    minWidth: "280px",
    maxWidth: "90vw",
    margin: "0 16px",
  },
}));

// Custom snackbar component that wraps the message
const CustomSnackbar = forwardRef(({ message, variant, ...props }, ref) => (
  <GlassmorphismSnackbar ref={ref} variant={variant} {...props}>
    <div className="SnackbarContent-message">{message}</div>
  </GlassmorphismSnackbar>
));

// Main provider component
export const GlassmorphismSnackbarProvider = ({ children, ...props }) => {
  return (
    <SnackbarProvider
      maxSnack={4}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      dense={false}
      preventDuplicate
      autoHideDuration={5000}
      Components={{
        success: CustomSnackbar,
        error: CustomSnackbar,
        warning: CustomSnackbar,
        info: CustomSnackbar,
        default: CustomSnackbar,
      }}
      sx={{
        // Container styling for better positioning
        "& .SnackbarContainer-root": {
          gap: "12px",
        },
      }}
      {...props}
    >
      {children}
    </SnackbarProvider>
  );
};
